import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";
import superagent from "superagent";

import {
  Button,
  Container,
  Form,
  Grid,
  Icon,
  Menu,
  Segment
} from 'semantic-ui-react';

import CreateInstitutionInviteForm from '_forms/institutionInviteForm';
import LineItem from '_views/lineItem';

import { AUTH_API_URL } from '../../../constants';
import { formatDate } from '_utils';
import { institutionInviteIsPending, institutionInvitePropType } from '_utils/invites';
import useCanAdminLicenses from '../../../hooks/useCanAdminLicenses';

const INVITE_VIEWS = {
  ADD: "add",
  LIST: "list"
};

const Invite = ({
  invite,
  onRemoveInvite
}) => {

  const canAdmin = useCanAdminLicenses();

  const [flashLink, setFlashLink] = useState(false);
  const [flashMail, setFlashMail] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  const isPending = institutionInviteIsPending(invite);

  const onRemove = () => {
    if (window.confirm(`Delete the Institutional Admin Invite for "${invite.first_name} ${invite.last_name}"?`)) {
      onRemoveInvite(invite._id)
    }
  };

  const onCopyLink = async () => {
    if (inviteLink) {
      try {
        await navigator.clipboard.writeText(inviteLink);
        setFlashLink(true);
      } catch (e) {
        window.alert("Could not copy link. Please try again.");
      }
    }
  };

  const onSendInviteEmail = () => {
    if (window.confirm(`Send the Institutional Admin Invite for "${invite.first_name} ${invite.last_name}" to "${invite.email}"?`)) {
      superagent
        .get(`${AUTH_API_URL}/institutions/${invite.institution_id}/invites/${invite._id}/send`)
        .withCredentials()
        .then(() => setFlashMail(true))
        .catch((err) => {
          const errorMessage =
            (err.response && err.response.body && err.response.body.error) ||
            "There was a problem sending the invitation to the user. Please try again.";
          window.alert(errorMessage);
        });
    }
  };

  useEffect(() => {
    if (!inviteLink && isPending) {
      superagent
        .get(`${AUTH_API_URL}/institutions/${invite.institution_id}/invites/${invite._id}/link`)
        .withCredentials()
        .then((res) => setInviteLink(res.body.url))
        .catch((err) => {
          const errorMessage =
            (err.response && err.response.body && err.response.body.error) ||
            "There was a problem retrieving the invite link. Please refresh and try again.";
          window.alert(errorMessage);
        });
    }
  }, []);

  useEffect(() => {
    const to = flashLink ? setTimeout(() => setFlashLink(false), 1000) : undefined;
    return () => clearTimeout(to);
  }, [flashLink]);

  useEffect(() => {
    const to = flashMail ? setTimeout(() => setFlashMail(false), 1000) : undefined;
    return () => clearTimeout(to);
  }, [flashMail]);

  return (
    <Segment>
      <Grid>
        <Grid.Row style={{ padding: '0.2rem 0' }} >
          <Grid.Column width={ 4 } >
            <LineItem label='Id' text={ invite._id } small />
          </Grid.Column>
          <Grid.Column width={ 6 } >
            <LineItem label='Created By' text={ invite.created_by.displayname } small />
          </Grid.Column>
          <Grid.Column width={ 6 } >
            <LineItem label='Created on' text={ formatDate(invite.created_on) } small />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ padding: '0.2rem 0' }} >
          <Grid.Column width={ 4 } >
            <LineItem
              label={ isPending ? 'Status' : 'Activated On' }
              text={ isPending ? 'Pending' : formatDate(invite.activated_on) }
              small
            />
          </Grid.Column>
          <Grid.Column width={ 4 } >
            <LineItem
              label='Activated User Id'
              text={ isPending ? '' : invite.activated_by._id }
              small
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={ 13 } >
            <Form size='tiny'>
              <Form.Group>
                <Form.Input width={ 5 } placeholder='First Name' readOnly value={ invite.first_name } />
                <Form.Input width={ 5 } placeholder='Last Name' readOnly value={ invite.last_name } />
                <Form.Input width={ 6 } placeholder='Email' readOnly value={ invite.email } />
              </Form.Group>
            </Form>
          </Grid.Column>
          <Grid.Column width={ 3 } >
            {
              canAdmin ? (
                <Button.Group size='tiny'>
                  <Button onClick={ onRemove } negative disabled={ !isPending } >
                    <Icon name='trash' />
                  </Button>
                  {inviteLink ? (
                    <Button onClick={ onCopyLink } disabled={ !isPending } positive={ flashLink } >
                      <Icon name={ flashLink ? 'check' : 'linkify' } />
                    </Button>
                  ) : null}
                  <Button onClick={ onSendInviteEmail } disabled={ !isPending } positive={ flashMail } >
                    <Icon name={ flashMail ? 'check' : 'mail' } />
                  </Button>
                </Button.Group>
              ) : null
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
}

Invite.propTypes = {
  invite: institutionInvitePropType.isRequired,
  onRemoveInvite: PropTypes.func.isRequired
};

const Invites = ({
  invites,
  onAddInstitutionInvite,
  onRemoveInstitutionInvite
}) => {

  const canAdmin = useCanAdminLicenses();
  const [activeView, setActiveView] = useState(INVITE_VIEWS.LIST);

  const onCancelInviteCreate = () => setActiveView(INVITE_VIEWS.LIST);
  const onSubmitInviteCreate = payload => {
    onAddInstitutionInvite(payload);
    setActiveView(INVITE_VIEWS.LIST);
  };

  return (
    <Container fluid>
      <Menu size='mini'>
        {
          canAdmin ? (
            <Menu.Item name='add_institution_pool'>
              <Button onClick={ () => setActiveView(INVITE_VIEWS.ADD) } disabled={ activeView === INVITE_VIEWS.ADD } >
                Create Admin Invite
              </Button>
            </Menu.Item>
          ) : null
        }
      </Menu>
      <Segment.Group>
        {
          activeView === INVITE_VIEWS.LIST ? (
            invites.map(invite => (
              <Invite key={ invite._id } invite={ invite } onRemoveInvite={ onRemoveInstitutionInvite } />
            ))
          ) : null
        }
        {
          activeView === INVITE_VIEWS.ADD ? (
            <CreateInstitutionInviteForm onCancel={ onCancelInviteCreate } onSubmit={ onSubmitInviteCreate } />
          ) : null
        }
      </Segment.Group>
    </Container>
  );
};

Invites.propTypes = {
  invites: PropTypes.arrayOf(institutionInvitePropType).isRequired, // arrayOf(licensePoolPropType).isRequired,
  onAddInstitutionInvite: PropTypes.func.isRequired,
  onRemoveInstitutionInvite: PropTypes.func.isRequired
};

export default Invites;



// WEBPACK FOOTER //
// ./webAdmin/components/institutions/institution/invites.js